import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import { DASHBOARD_ACCENT, resolveDashboardThemeMode } from './dashboardSurfaceSx';
export { WORKSPACE_STAT_COLORS } from './roomBoardStatusColors';

export function getDashboardWorkspaceCardSx(themeMode, options = {}) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  const {
    minHeight = { xs: 320, lg: 0 },
    maxHeight = '100%',
  } = options;

  return {
    width: '100%',
    flex: 1,
    height: '100%',
    minHeight,
    maxHeight,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 2,
    overflow: 'hidden',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : 'linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)',
    boxShadow: isDark
      ? '0 18px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)'
      : '0 12px 28px rgba(15,23,42,0.08)',
  };
}

export function getDashboardWorkspaceHeaderSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    px: 1.5,
    py: 1.25,
    borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
    flexShrink: 0,
  };
}

export function getDashboardWorkspaceTitleSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    color: isDark ? '#f8fafc' : '#0f172a',
    fontWeight: 800,
    fontSize: '0.95rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  };
}

export function getDashboardWorkspaceSubtitleSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    color: isDark ? alpha('#cbd5e1', 0.72) : '#64748b',
    fontSize: '0.68rem',
    mt: 0.25,
  };
}

export function getDashboardWorkspaceSummaryChipSx(themeMode, color) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    height: 24,
    fontSize: '0.62rem',
    fontWeight: 700,
    borderRadius: 999,
    color: isDark ? color : alpha(color, 0.95),
    border: `1px solid ${alpha(color, isDark ? 0.36 : 0.32)}`,
    bgcolor: isDark ? alpha(color, 0.16) : alpha(color, 0.1),
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    boxShadow: isDark
      ? `inset 0 1px 0 ${alpha('#fff', 0.08)}`
      : `inset 0 1px 0 ${alpha('#fff', 0.55)}`,
    '& .MuiChip-label': { px: 0.85 },
  };
}

export function getDashboardWorkspaceSectionHeaderSx(themeMode, color = DASHBOARD_ACCENT) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    px: 1.25,
    py: 0.85,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    flexWrap: 'wrap',
    borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.06) : alpha('#000', 0.06)}`,
    bgcolor: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
  };
}

export function getDashboardWorkspaceSectionTitleSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    color: isDark ? '#e2e8f0' : '#1e293b',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  };
}

export function getDashboardWorkspaceScrollSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    flex: '1 1 0',
    minHeight: 0,
    height: 0,
    overflow: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    // Isolate scroll paint from the rest of the dashboard chrome.
    contain: 'layout paint style',
    '&::-webkit-scrollbar': { width: 6, height: 6 },
    '&::-webkit-scrollbar-thumb': {
      background: isDark ? alpha('#ffffff', 0.14) : alpha('#000', 0.14),
      borderRadius: 999,
    },
  };
}

export function getDashboardWorkspaceToolbarSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    px: 1.25,
    py: 1,
    borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.06) : alpha('#000', 0.06)}`,
    bgcolor: isDark ? THEME_COLORS.BACKGROUND_DARK : alpha('#000', 0.02),
    flexShrink: 0,
  };
}

export function getDashboardWorkspaceRowSx(themeMode, accentColor) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0.85,
    px: 1.25,
    py: 0.9,
    cursor: 'pointer',
    borderLeft: `3px solid ${accentColor}`,
    borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.05) : alpha('#000', 0.05)}`,
    transition: 'background-color 0.15s ease, transform 0.15s ease',
    '&:hover': {
      bgcolor: isDark ? alpha(accentColor, 0.1) : alpha(accentColor, 0.06),
      transform: 'translateX(2px)',
    },
  };
}

export function getDashboardWorkspaceAvatarSx(color) {
  return {
    width: 30,
    height: 30,
    fontSize: '0.72rem',
    fontWeight: 800,
    bgcolor: alpha(color, 0.16),
    color,
    border: `1px solid ${alpha(color, 0.28)}`,
  };
}

export function getDashboardWorkspaceItemTitleSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    color: isDark ? '#f8fafc' : '#0f172a',
    fontSize: '0.78rem',
    fontWeight: 700,
    lineHeight: 1.25,
  };
}

export function getDashboardWorkspaceItemMetaSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    color: isDark ? alpha('#cbd5e1', 0.72) : '#64748b',
    fontSize: '0.66rem',
    lineHeight: 1.35,
  };
}

export function getDashboardWorkspaceBadgeSx(color) {
  return {
    height: 18,
    fontSize: '0.58rem',
    fontWeight: 700,
    borderRadius: 999,
    color,
    border: `1px solid ${alpha(color, 0.35)}`,
    bgcolor: alpha(color, 0.12),
    '& .MuiChip-label': { px: 0.65 },
  };
}

export function getDashboardWorkspaceIconButtonSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    color: isDark ? 'rgba(148,163,184,0.9)' : '#64748b',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
    borderRadius: '10px',
  };
}

export function getDashboardWorkspaceInnerSectionSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.05) : alpha('#000', 0.05)}`,
  };
}
